import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CalendarCheck, Save, Pencil, Trash2, BarChart3 } from "lucide-react";

const STATUS = ["Presente", "Ausente", "Ausente justificado"];
const STATUS_COLORS = {
  "Presente": "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  "Ausente": "bg-red-500/20 text-red-300 border-red-500/40",
  "Ausente justificado": "bg-amber-500/20 text-amber-300 border-amber-500/40",
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonth() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

export default function AdminAttendance() {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chamada
  const [date, setDate] = useState(todayISO());
  const [turma, setTurma] = useState("__all__");
  const [entries, setEntries] = useState({}); // athlete_id -> {status, note}
  const [saving, setSaving] = useState(false);

  // Histórico
  const [histFrom, setHistFrom] = useState(firstOfMonth());
  const [histTo, setHistTo] = useState(todayISO());
  const [histRecords, setHistRecords] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [editRec, setEditRec] = useState(null);

  // Percentuais
  const [summary, setSummary] = useState([]);
  const [sumLoading, setSumLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/athletes");
        setAthletes(data);
      } catch (err) {
        console.error("[attendance] falha ao carregar atletas:", err);
        toast.error("Falha ao carregar atletas");
      } finally { setLoading(false); }
    })();
  }, []);

  const turmas = useMemo(() => {
    const s = new Set();
    for (const a of athletes) {
      const t = (a.class_name || "").trim();
      if (t) s.add(t);
    }
    return Array.from(s).sort();
  }, [athletes]);

  const filteredAthletes = useMemo(() => {
    if (turma === "__all__") return athletes;
    if (turma === "__none__") return athletes.filter(a => !((a.class_name || "").trim()));
    return athletes.filter(a => (a.class_name || "").trim() === turma);
  }, [athletes, turma]);

  // Carrega chamada existente para (date + filtro) para pré-preencher
  useEffect(() => {
    if (!date) return;
    (async () => {
      try {
        const params = { date };
        if (turma !== "__all__" && turma !== "__none__") params.class_name = turma;
        const { data } = await api.get("/attendance", { params });
        const map = {};
        for (const r of data) map[r.athlete_id] = { status: r.status, note: r.note || "" };
        setEntries(map);
      } catch (err) {
        console.error("[attendance] falha ao pré-carregar chamada:", err);
      }
    })();
  }, [date, turma]);

  const setEntry = (aid, patch) => {
    setEntries(prev => ({ ...prev, [aid]: { ...(prev[aid] || { status: "", note: "" }), ...patch } }));
  };

  const markAll = (status) => {
    const next = { ...entries };
    for (const a of filteredAthletes) next[a.id] = { ...(next[a.id] || { note: "" }), status };
    setEntries(next);
  };

  const saveChamada = async () => {
    const payload = filteredAthletes
      .filter(a => entries[a.id]?.status)
      .map(a => ({ athlete_id: a.id, status: entries[a.id].status, note: entries[a.id].note || "" }));
    if (!payload.length) { toast.error("Marque ao menos um atleta antes de salvar."); return; }
    setSaving(true);
    try {
      await api.post("/attendance/bulk", { date, entries: payload });
      toast.success(`Chamada salva (${payload.length} atletas)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao salvar chamada");
    } finally { setSaving(false); }
  };

  const loadHistorico = async () => {
    setHistLoading(true);
    try {
      const params = { date_from: histFrom, date_to: histTo };
      if (turma !== "__all__" && turma !== "__none__") params.class_name = turma;
      const { data } = await api.get("/attendance", { params });
      setHistRecords(data);
    } catch (err) {
      toast.error("Falha ao carregar histórico");
      console.error("[attendance] histórico:", err);
    } finally { setHistLoading(false); }
  };

  const loadSummary = async () => {
    setSumLoading(true);
    try {
      const params = { date_from: histFrom, date_to: histTo };
      if (turma !== "__all__" && turma !== "__none__") params.class_name = turma;
      const { data } = await api.get("/attendance/summary", { params });
      setSummary(data);
    } catch (err) {
      toast.error("Falha ao carregar percentuais");
      console.error("[attendance] summary:", err);
    } finally { setSumLoading(false); }
  };

  const saveEditRec = async () => {
    if (!editRec) return;
    try {
      await api.put(`/attendance/${editRec.id}`, {
        athlete_id: editRec.athlete_id, date: editRec.date,
        status: editRec.status, note: editRec.note || "",
      });
      toast.success("Registro atualizado");
      setEditRec(null);
      await loadHistorico();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao atualizar");
    }
  };

  const deleteRec = async (r) => {
    if (!window.confirm(`Excluir chamada de ${r.date}?`)) return;
    try {
      await api.delete(`/attendance/${r.id}`);
      setHistRecords(prev => prev.filter(x => x.id !== r.id));
      toast.success("Registro removido");
    } catch { toast.error("Erro ao remover"); }
  };

  const athleteName = (aid) => athletes.find(a => a.id === aid)?.name || aid;

  return (
    <AppLayout kind="admin">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold flex items-center gap-3">
            <CalendarCheck className="w-8 h-8 text-amber-400" />
            Frequência / Chamada
          </h1>
          <p className="text-slate-400 mt-1">Registre a chamada por turma e data, edite históricos e consulte percentuais.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="space-y-2">
          <Label className="text-slate-300">Data</Label>
          <Input type="date" value={date} onChange={(e)=>setDate(e.target.value)} data-testid="att-date-input"
            className="bg-[#0D2A52] border-amber-500/20 text-white" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Turma</Label>
          <Select value={turma} onValueChange={setTurma}>
            <SelectTrigger data-testid="att-turma-select" className="bg-[#0D2A52] border-amber-500/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#071A33] text-white border-amber-500/20">
              <SelectItem value="__all__">Todas as turmas</SelectItem>
              <SelectItem value="__none__">Sem turma cadastrada</SelectItem>
              {turmas.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          {!turmas.length && (
            <p className="text-[11px] text-slate-500">Dica: informe a "Turma" ao editar o atleta em <b>Atletas</b>.</p>
          )}
        </div>
        <div className="space-y-2 flex items-end">
          <div className="text-xs text-slate-400">Atletas nesta seleção: <span className="text-amber-300 font-mono">{filteredAthletes.length}</span></div>
        </div>
      </div>

      <Tabs defaultValue="chamada" className="w-full">
        <TabsList className="bg-[#071A33] border border-amber-500/20 mb-4">
          <TabsTrigger value="chamada" data-testid="tab-chamada" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-300">Chamada</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-300">Histórico</TabsTrigger>
          <TabsTrigger value="percentuais" data-testid="tab-percentuais" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-300">Percentuais</TabsTrigger>
        </TabsList>

        {/* Chamada */}
        <TabsContent value="chamada">
          <Card className="bg-transparent gsx-card-dark p-5">
            <div className="flex flex-wrap gap-2 mb-4">
              <Button size="sm" onClick={()=>markAll("Presente")} data-testid="mark-all-present"
                className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30">Marcar todos Presentes</Button>
              <Button size="sm" onClick={()=>markAll("Ausente")} data-testid="mark-all-absent"
                className="bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30">Marcar todos Ausentes</Button>
              <div className="ml-auto">
                <Button onClick={saveChamada} disabled={saving || loading} data-testid="save-chamada-btn"
                  className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Salvando…" : "Salvar Chamada"}
                </Button>
              </div>
            </div>

            {loading && <div className="text-slate-400 py-8 text-center">Carregando atletas…</div>}
            {!loading && !filteredAthletes.length && (
              <div className="text-slate-400 py-8 text-center border border-dashed border-white/10 rounded-lg">
                Nenhum atleta nessa seleção.
              </div>
            )}
            <div className="space-y-2">
              {filteredAthletes.map(a => {
                const st = entries[a.id]?.status || "";
                return (
                  <div key={a.id} className="grid md:grid-cols-[1fr_auto_1fr] gap-3 p-3 rounded-lg border border-white/10 bg-[#0D2A52]/40 items-center"
                    data-testid={`att-row-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{a.name}</div>
                      <div className="text-xs text-slate-400 truncate">{a.class_name || "Sem turma"} • {a.category || "—"}</div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {STATUS.map(s => (
                        <button key={s} type="button" onClick={()=>setEntry(a.id, { status: s })}
                          data-testid={`att-${a.id}-${s}`}
                          className={`text-[11px] px-2.5 py-1.5 rounded border transition ${
                            st === s ? STATUS_COLORS[s] + " font-semibold"
                                     : "border-white/10 text-slate-300 hover:bg-white/5"
                          }`}>
                          {s === "Presente" ? "P" : s === "Ausente" ? "A" : "J"}
                        </button>
                      ))}
                    </div>
                    <Input value={entries[a.id]?.note || ""} onChange={(e)=>setEntry(a.id, { note: e.target.value })}
                      placeholder="Observação (opcional)"
                      data-testid={`att-note-${a.id}`}
                      className="bg-[#071A33] border-amber-500/20 text-white text-sm h-9" />
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* Histórico */}
        <TabsContent value="historico">
          <Card className="bg-transparent gsx-card-dark p-5">
            <div className="flex flex-wrap gap-3 items-end mb-4">
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">De</Label>
                <Input type="date" value={histFrom} onChange={(e)=>setHistFrom(e.target.value)}
                  className="bg-[#0D2A52] border-amber-500/20 text-white h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Até</Label>
                <Input type="date" value={histTo} onChange={(e)=>setHistTo(e.target.value)}
                  className="bg-[#0D2A52] border-amber-500/20 text-white h-9" />
              </div>
              <Button onClick={loadHistorico} disabled={histLoading} data-testid="load-historico-btn"
                className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold h-9">
                {histLoading ? "Carregando…" : "Consultar"}
              </Button>
            </div>
            {!histRecords.length && !histLoading && (
              <div className="text-slate-400 py-6 text-center border border-dashed border-white/10 rounded-lg">
                Nenhum registro no período. Clique em Consultar após ajustar as datas.
              </div>
            )}
            <div className="space-y-2">
              {histRecords.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/10 bg-[#0D2A52]/40"
                  data-testid={`hist-row-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm truncate">
                      {r.date} • <span className="text-amber-300">{athleteName(r.athlete_id)}</span>
                    </div>
                    {r.note && <div className="text-xs text-slate-400 truncate">{r.note}</div>}
                  </div>
                  <span className={`text-[11px] uppercase tracking-wide px-2 py-1 rounded border ${STATUS_COLORS[r.status] || ""}`}>
                    {r.status}
                  </span>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={()=>setEditRec({ ...r })}
                      data-testid={`hist-edit-${r.id}`}
                      className="border border-amber-400/70 bg-amber-500/15 text-amber-200 hover:bg-amber-400 hover:text-[#071A33] hover:border-amber-300 h-8 w-8">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={()=>deleteRec(r)}
                      data-testid={`hist-delete-${r.id}`}
                      className="text-red-400 hover:bg-red-500/10 h-8 w-8">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Percentuais */}
        <TabsContent value="percentuais">
          <Card className="bg-transparent gsx-card-dark p-5">
            <div className="flex flex-wrap gap-3 items-end mb-4">
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">De</Label>
                <Input type="date" value={histFrom} onChange={(e)=>setHistFrom(e.target.value)}
                  className="bg-[#0D2A52] border-amber-500/20 text-white h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Até</Label>
                <Input type="date" value={histTo} onChange={(e)=>setHistTo(e.target.value)}
                  className="bg-[#0D2A52] border-amber-500/20 text-white h-9" />
              </div>
              <Button onClick={loadSummary} disabled={sumLoading} data-testid="load-summary-btn"
                className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold h-9">
                <BarChart3 className="w-4 h-4 mr-1" /> {sumLoading ? "Calculando…" : "Calcular Percentuais"}
              </Button>
            </div>
            {!summary.length && !sumLoading && (
              <div className="text-slate-400 py-6 text-center border border-dashed border-white/10 rounded-lg">
                Clique em "Calcular Percentuais" para carregar os dados.
              </div>
            )}
            <div className="space-y-2">
              {summary.map(s => (
                <div key={s.athlete_id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/10 bg-[#0D2A52]/40"
                  data-testid={`summary-row-${s.athlete_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-medium truncate">{s.name}</div>
                    <div className="text-xs text-slate-400 truncate">{s.class_name || "Sem turma"} • {s.category || "—"}</div>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <div><div className="text-slate-400">Presenças</div><div className="text-emerald-300 font-mono">{s.present}</div></div>
                    <div><div className="text-slate-400">Justif.</div><div className="text-amber-300 font-mono">{s.justified}</div></div>
                    <div><div className="text-slate-400">Ausentes</div><div className="text-red-300 font-mono">{s.absent}</div></div>
                    <div><div className="text-slate-400">Total</div><div className="text-white font-mono">{s.total}</div></div>
                    <div className="min-w-[50px] text-right">
                      <div className="text-slate-400">%</div>
                      <div className="font-mono text-amber-400 text-lg" data-testid={`summary-pct-${s.athlete_id}`}>{s.percentage}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">
              Cálculo: Presente = 100%, Ausente justificado = 50%, Ausente = 0%. Base: total de chamadas registradas no período.
            </p>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      {editRec && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={()=>setEditRec(null)}>
          <Card className="gsx-app-modal-card bg-[#071A33] border-amber-500/30 p-6 max-w-md w-full" onClick={(e)=>e.stopPropagation()}>
            <h3 className="text-white font-semibold mb-4">Editar chamada — {athleteName(editRec.athlete_id)}</h3>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Data</Label>
                <Input type="date" value={editRec.date} onChange={(e)=>setEditRec({...editRec, date: e.target.value})}
                  className="bg-[#0D2A52] border-amber-500/20 text-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Status</Label>
                <Select value={editRec.status} onValueChange={(v)=>setEditRec({...editRec, status: v})}>
                  <SelectTrigger className="bg-[#0D2A52] border-amber-500/20 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#071A33] text-white border-amber-500/20">
                    {STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Observação</Label>
                <Textarea rows={2} value={editRec.note || ""}
                  onChange={(e)=>setEditRec({...editRec, note: e.target.value})}
                  className="bg-[#0D2A52] border-amber-500/20 text-white" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={()=>setEditRec(null)}
                  className="border-amber-500/30 bg-transparent text-amber-300 hover:bg-amber-500/10">Cancelar</Button>
                <Button onClick={saveEditRec} data-testid="edit-save-btn"
                  className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">Salvar</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </AppLayout>
  );
}
